
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, ShoppingBag, Truck } from "lucide-react";
import { InventoryItem } from "@/store/api/inventoryStore";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface InventoryStatsProps {
  items: InventoryItem[];
}

export function InventoryStats({ items }: InventoryStatsProps) {
  const { t } = useTranslation(["inventory", "common"]);
  const { formatCurrency } = useCurrency();
  const isMobile = useIsMobile();
  
  // Calculate statistics
  const totalItems = items.reduce((acc, item) => acc + item.stock, 0);
  const availableItems = items.reduce((acc, item) => acc + item.available, 0);
  const reservedItems = items.reduce((acc, item) => acc + item.reserved, 0);
  const totalValue = items.reduce((acc, item) => acc + (item.price * item.stock), 0);
  
  return (
    <div className="grid gap-2 md:gap-4 grid-cols-3">
      <Card className={isMobile ? "p-1" : ""}>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? "p-2 pb-0" : "pb-2"}`}>
          <CardTitle className={`${isMobile ? "text-[9px]" : "text-sm"} font-medium`}>
            {t('totalStock')}
          </CardTitle>
          <Package className={`${isMobile ? "h-2 w-2" : "h-4 w-4"} text-muted-foreground`} />
        </CardHeader>
        <CardContent className={isMobile ? "p-2 pt-0" : ""}>
          <div className={`${isMobile ? "text-base" : "text-2xl"} font-bold`}>{totalItems}</div>
          <div className={`flex items-center space-x-1 ${isMobile ? "mt-0" : "mt-1"}`}>
            <Badge variant="secondary" className={isMobile ? "text-[8px] px-1 py-0 h-3" : ""}>{t('items')}</Badge>
            <Badge variant="outline" className={isMobile ? "text-[8px] px-1 py-0 h-3" : ""}>{items.length} {t('uniqueItems')}</Badge>
          </div>
        </CardContent>
      </Card>
      
      <Card className={isMobile ? "p-1" : ""}>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? "p-2 pb-0" : "pb-2"}`}>
          <CardTitle className={`${isMobile ? "text-[9px]" : "text-sm"} font-medium`}>
            {t('availability')}
          </CardTitle>
          <ShoppingBag className={`${isMobile ? "h-2 w-2" : "h-4 w-4"} text-muted-foreground`} />
        </CardHeader>
        <CardContent className={isMobile ? "p-2 pt-0" : ""}>
          <div className={`${isMobile ? "text-base" : "text-2xl"} font-bold`}>{availableItems}</div>
          <div className={`flex items-center space-x-1 ${isMobile ? "mt-0" : "mt-1"}`}>
            <Badge variant="secondary" className={isMobile ? "text-[8px] px-1 py-0 h-3" : ""}>{t('available')}</Badge>
            <Badge variant="outline" className={isMobile ? "text-[8px] px-1 py-0 h-3" : ""}>{reservedItems} {t('reserved')}</Badge>
          </div>
        </CardContent>
      </Card>
      
      <Card className={isMobile ? "p-1" : ""}>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? "p-2 pb-0" : "pb-2"}`}>
          <CardTitle className={`${isMobile ? "text-[9px]" : "text-sm"} font-medium`}>
            {t('totalValue')}
          </CardTitle>
          <Truck className={`${isMobile ? "h-2 w-2" : "h-4 w-4"} text-muted-foreground`} />
        </CardHeader>
        <CardContent className={isMobile ? "p-2 pt-0" : ""}>
          <div className={`${isMobile ? "text-base" : "text-2xl"} font-bold`}>{formatCurrency(totalValue)}</div>
          <div className={`${isMobile ? "text-[8px]" : "text-xs"} text-muted-foreground ${isMobile ? "mt-0" : "mt-1"}`}>
            {t('calculatedUsingCurrentPrices')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
